/**
 * A class which represents a New Form Design Input widget.
 * This defines the structure and the main behavior of the widget.
 *
 * The Input has a special feature which requires some additional cooperation with its features. To increase the
 * customization level a feature can extend or overwrite the parameters of a context object provided by the widget.
 *
 * Context items:
 * -fieldSyncFunction: A feature could change the value of the original field on blur.
 *                     function(originalInputField, visibleInputField)
 *
 * @param $markup The jQuery element which is the Input markup.
 */

import '../styles/input.less';
import '../features/styles/formatter.less';
import $ from 'jquery';
import { merge } from '../../../loader/util';
import * as common from '../../../shared/modules/common';
import { isInputFiredOnPlaceholderDelete } from '../../../shared/modules/support';
import { render } from 'mustache';
import view from './inputView';
import { init as initMessageUtil, message as _message } from './inputMessageUtil';
import { init as initFormatterFeature } from '../features/js/formatter';
import { addMutationHandlers } from '../../../shared/modules/domchange';

function Input(markup) {
    var $markup = $(markup),
        $view,
        mutationObserver,
        behaviorContext;

    /**
     * @return the placeholder text of the input field
     */
    function getPlaceholderText() {
        var placeholderText = markup.getAttribute('placeholder');
        if (placeholderText) {
            markup.setAttribute('data-placeholder', placeholderText);
            markup.removeAttribute('placeholder');
        }
        return placeholderText || markup.getAttribute('data-placeholder');
    }

    /**
     * @return the label text of the input field
     */
    function getLabelText() {
        return $markup.attr('data-label');
    }

    /**
     * @param visibleInputField Optional. The event which can influence the state outcome. (For focus/blur).
     * @return the state of the input field
     */
    /* eslint-disable complexity */
    function getState(stateHolderElement) {
        var disabled = stateHolderElement.hasAttribute('disabled') ? 'disabled' : undefined,
            readonly = stateHolderElement.hasAttribute('readonly') ? 'readonly' : undefined,
            focus = common.hasFocus(stateHolderElement) ? 'focus' : undefined;
        return disabled || readonly || focus || 'blur';
    }
    /* eslint-enable complexity */

    function addAllEventListeners(target) {
        target.addEventListener('input', inputHandler);
        target.addEventListener('change', inputHandler);
        if (common.isIe9()) {
            target.addEventListener('keyup', inputHandler);
            target.addEventListener('blur', inputHandler);
        }
        target.addEventListener('focus', stateHandler);
        target.addEventListener('blur', stateHandler);
    }

    function removeAllEventListeners(target) {
        target.removeEventListener('input', inputHandler);
        target.removeEventListener('change', inputHandler);
        if (common.isIe9()) {
            target.removeEventListener('keyup', inputHandler);
            target.removeEventListener('blur', inputHandler);
        }
        target.removeEventListener('focus', stateHandler);
        target.removeEventListener('blur', stateHandler);
    }

    /**
     * Input handler
     */
    function inputHandler(event) {
        var eventTarget = event.target,
            hasValue = !!eventTarget.value.length;
        if (!isInputFiredOnPlaceholderDelete(event) || !common.isNodeDetached(eventTarget)) {
            updateLabelTextsInView(getView(), hasValue);
        }
    }

    function stateHandler(event) {
        var state = getState(event.target);
        $view.removeClass('uwr-input-blur uwr-input-focus uwr-input-readonly uwr-input-disabled');
        $view.addClass('uwr-input-' + state);
    }

    function getButtonIconSelector() {
        return common.data($markup, 'icon-button-selector');
    }

    function setButtonIcon() {
        var iconButtonSelector = getButtonIconSelector(),
            button = document.querySelector(iconButtonSelector),
            afterPlaceholderBox = $('.uwr-input-after', $view).get(0);

        if (iconButtonSelector) {
            /* TODO
             * button replacement or different mechanism to add or remove button (multiple button support required)
             */
            if (afterPlaceholderBox && button) {
                common.addClass(button, 'uwr-input-icon');
                afterPlaceholderBox.appendChild(button);
                common.addClass(afterPlaceholderBox.parentNode, 'uwr-input-has-button');
            } else {
                common.removeClass(afterPlaceholderBox.parentNode, 'uwr-input-has-button');
            }
        }
    }

    function updateLabelTextsInView(inputViewContainer, hasValue) {
        var labelText = getLabelText() || '',
            placeholderText = getPlaceholderText() || '',
            label = inputViewContainer.querySelector('.uwr-input-label'),
            placeholder = inputViewContainer.querySelector('.uwr-input-placeholder');

        if (hasValue) {
            common.addClass(inputViewContainer, 'uwr-input-has-value');
        } else {
            common.removeClass(inputViewContainer, 'uwr-input-has-value');
        }

        if (!placeholderText) {
            if (hasValue) {
                common.removeClass(label, 'uwr-input-label-bottom');
                common.addClass(label, 'uwr-input-label-top');
            } else {
                common.removeClass(label, 'uwr-input-label-top');
                common.addClass(label, 'uwr-input-label-bottom');
            }
        }

        common.textContent(placeholder, placeholderText);
        common.textContent(label, labelText);
    }

    function getView() {
        return $markup.closest('.uwr-input-container')[0];
    }

    function handleValueChange() {
        updateLabelTextsInView(getView(), markup.value.length > 0);
    }

    /**
     * Initialize widget view
     */
    function init() {
        var markup = $markup.get(0);

        if (!common.shouldBeInitialized(markup)) {
            return;
        }

        behaviorContext = {
            visibleInputField: markup
        };

        $view = $(render(view.inputContainer, {
            state: getState(markup),
            value: $markup.value,
            messageIcon: common.data(markup, 'message')
        }));
        updateLabelTextsInView($view[0], !!$markup.val().length);
        $markup.after($view);
        $view.find('.uwr-input-content').prepend($markup);

        // initialize icon button before MessageBox to proper tip position
        setButtonIcon();

        // Run feature initialization before adding any listeners (feature changes should run before acting here)
        merge(behaviorContext,
            initMessageUtil($markup),
            initFormatterFeature($markup)
        );

        // The visibleInputField is set or modified by the features. Now it can be decorated.
        common.addClass(behaviorContext.visibleInputField, 'uwr-input-visible-input');
        if (getState(behaviorContext.visibleInputField) !== 'disabled') {
            addAllEventListeners(behaviorContext.visibleInputField);
        }

        mutationObserver = addMutationHandlers(markup, {
            node: {
                'data-message': function (mutation) {
                    initMessageUtil($(mutation.target));
                },
                'data-label': function (mutation) {
                    updateLabelTextsInView(getView(), mutation.target.value);
                },
                'data-placeholder': function (mutation) {
                    updateLabelTextsInView(getView(), mutation.target.value);
                },
                'placeholder': function (mutation) {
                    updateLabelTextsInView(getView(), mutation.target.value);
                },
                'data-icon-button-selector': setButtonIcon,
                'disabled': function (mutation) {
                    var view = $view.get(0);
                    if (mutation.target === null) { // attribute removed
                        common.removeClass(view, 'uwr-input-disabled');
                        common.addClass(view, 'uwr-input-' + getState(markup));
                        addAllEventListeners(behaviorContext.visibleInputField);
                    } else {
                        common.removeClass(view, 'uwr-input-blur');
                        common.removeClass(view, 'uwr-input-focus');
                        common.removeClass(view, 'uwr-input-readonly');
                        common.addClass(view, 'uwr-input-disabled');
                        removeAllEventListeners(behaviorContext.visibleInputField);
                    }
                }
            },
            properties: {
                value: handleValueChange
            }
        });

        common.markInitialized(markup);

        return this;
    }

    function cleanup() {
        if (mutationObserver) {
            mutationObserver.disconnect();
        }
        removeAllEventListeners(behaviorContext.visibleInputField);
        common.unwrapMarkup(markup, $view[0]);
        common.removeClass(markup, 'uwr-input-visible-input');
        common.unmarkInitialized(markup);

        return this;
    }

    return {
        init: init,
        cleanup: cleanup
    };
}

/**
 * Initialize the selected Input(s)
 * Supported use cases:
 *   - init();
 *   - init(callback);
 *   - init(selector);
 *   - init(selector, callback);
 * @param selector CSS selector string that returns the elements that need to be initialized.
 *   Optional: If not passed in, then all elements marked with "uwr-input" CSS class will be processed.
 * @param callback The function to run after initialization (optional).
 */
export var init = common.initWidget.bind(null, '.uwr-input', Input);

export var message = _message;



//////////////////
// WEBPACK FOOTER
// ./widgets/Input/js/input.js
// module id = 4
// module chunks = main